import Vue from "vue";
import DatePicker from "vue2-datepicker";
import SortedTablePlugin from "vue-sorted-table";
import moment from "moment";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { TH_SARABUN_TTF_BASE64 } from "@/assets/fonts/thsarabun.js";

Vue.use(SortedTablePlugin, {
  ascIcon: '<i class="mdi mdi-arrow-down"></i>',
  descIcon: '<i class="mdi mdi-arrow-up"></i>',
});

export default {
  components: {
    DatePicker,
  },
  data: function () {
    return {
      busy: false,
      sortBy: "document_date",
      sortDesc: false,
      apiStatus: "กรุณาระบุวันที่และกดปุ่มแสดงข้อมูลเพื่อค้นหาข้อมูล",
      fields: [
        {
          key: "index",
          label: "#",
          thClass: "font-weight-bold text-xs-1/2",
          tdClass: "td-top text-xs-1/2",
          sortable: false,
        },
        {
          key: "document_date",
          label: "วันที่",
          thClass: "font-weight-bold text-xs-1/2",
          tdClass: "td-top text-xs-1/2",
          sortable: true,
        },
        {
          key: "document_no",
          label: "เลขที่เอกสาร",
          thClass: "font-weight-bold text-xs-1/2",
          tdClass: "td-top text-xs-1/2",
          sortable: true,
        },
        {
          key: "type",
          label: "ประเภท",
          thClass: "font-weight-bold text-xs-1/2",
          tdClass: "td-top text-xs-1/2",
          sortable: true,
        },
        {
          key: "company_name",
          label: "ชื่อลูกค้า/บริษัท",
          thClass: "font-weight-bold text-xs-1/2",
          tdClass: "td-top text-xs-1/2",
          sortable: true,
        },
        {
          key: "tax_id",
          label: "เลขประจำตัวผู้เสียภาษี",
          thClass: "font-weight-bold text-xs-1/2",
          tdClass: "td-top text-xs-1/2",
          sortable: false,
        },
        {
          key: "subtotal",
          label: "มูลค่าสินค้า",
          thClass: "font-weight-bold text-xs-1/2",
          tdClass: "font-weight-bold text-black td-top text-xs-1/2",
          class: "text-right",
          sortable: true,
        },
        {
          key: "vat",
          label: "VAT",
          thClass: "font-weight-bold text-xs-1/2",
          tdClass: "font-weight-bold text-black td-top text-xs-1/2",
          class: "text-right",
          sortable: true,
        },
        {
          key: "grand_total",
          label: "รวมทั้งสิ้น",
          thClass: "font-weight-bold text-xs-1/2",
          tdClass: "font-weight-bold text-black td-top text-xs-1/2",
          class: "text-right",
          sortable: true,
        },
      ],
      items: [],
      summary: null,
      form: {
        start_date: new Date(moment().startOf("month").format("YYYY-MM-DD")),
        end_date: new Date(moment().endOf("month").format("YYYY-MM-DD")),
        type: "all",
      },
    };
  },
  methods: {
    formatCurrency(val) {
      return parseFloat(val || 0).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    async onInitData() {
      try {
        this.items = [];
        this.summary = null;
        this.busy = true;
        this.apiStatus = "กำลังค้นหาข้อมูล..";

        const res = await this.$axios.get(
          this.$host +
            "/api/v1/document/vat-sales-report?start_date=" +
            moment(this.form.start_date).format("YYYY-MM-DD") +
            "&end_date=" +
            moment(this.form.end_date).format("YYYY-MM-DD") +
            "&type=" +
            this.form.type
        );

        this.busy = false;
        this.apiStatus = "ไม่พบข้อมูล";
        this.items = res.data.data;
        this.summary = res.data.summary;
      } catch (error) {
        this.busy = false;
        this.apiStatus = "ไม่พบข้อมูล";
        this.onExceptionHandler((error.response && error.response.data && error.response.data.message) || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
      }
    },

    addThaiFont(doc) {
      if (!doc || !TH_SARABUN_TTF_BASE64) return;
      doc.addFileToVFS("THSarabun.ttf", TH_SARABUN_TTF_BASE64);
      doc.addFont("THSarabun.ttf", "THSarabun", "normal");
      doc.addFont("THSarabun.ttf", "THSarabun", "bold");
    },

    exportPDF() {
      if (!this.items || this.items.length === 0) {
        this.$bvToast.toast("ไม่มีข้อมูลให้ส่งออก", {
          title: "Export PDF",
          variant: "warning",
          solid: true,
        });
        return;
      }

      const doc = new jsPDF({
        orientation: "landscape",
        unit: "pt",
        format: "a4",
      });
      this.addThaiFont(doc);
      doc.setFont("THSarabun", "normal");

      const margin = { top: 80, right: 36, bottom: 48, left: 36 };
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();

      const title = "รายงาน VAT ขาย";
      const period = `ช่วงวันที่: ${moment(this.form.start_date).format(
        "DD/MM/YYYY"
      )} - ${moment(this.form.end_date).format("DD/MM/YYYY")}`;

      const drawHeader = () => {
        doc.setFontSize(18);
        doc.setFont("THSarabun", "bold");
        doc.text(title, margin.left, 40);
        doc.setFontSize(12);
        doc.setFont("THSarabun", "normal");
        doc.text(period, margin.left, 60);
      };
      const drawFooter = (n) => {
        doc.setFontSize(11);
        doc.text(`หน้า ${n}`, pageW - margin.right, pageH - 20, {
          align: "right",
        });
      };

      let pageNo = 1;
      drawHeader();
      drawFooter(pageNo);

      const money = (n) => this.formatCurrency(n);

      const head = [
        ["#", "วันที่", "เลขที่เอกสาร", "ประเภท", "ชื่อลูกค้า/บริษัท", "เลขผู้เสียภาษี", "มูลค่าสินค้า", "VAT", "รวมทั้งสิ้น"],
      ];

      const col = {
        0: { cellWidth: 24, halign: "right" },
        1: { cellWidth: 60 },
        2: { cellWidth: 100 },
        3: { cellWidth: 55 },
        4: { cellWidth: 180 },
        5: { cellWidth: 90 },
        6: { cellWidth: 75, halign: "right" },
        7: { cellWidth: 65, halign: "right" },
        8: { cellWidth: 75, halign: "right" },
      };

      const body = this.items.map((it, i) => [
        i + 1,
        new Date(it.document_date).toLocaleDateString("th-TH", {
          year: "2-digit",
          month: "short",
          day: "numeric",
        }),
        it.document_no || "-",
        it.type === "abb" ? "ย่อ" : "เต็มรูปแบบ",
        it.company_name || "-",
        it.tax_id || "-",
        money(it.subtotal),
        money(it.vat),
        money(it.grand_total),
      ]);

      doc.autoTable({
        startY: margin.top,
        head,
        body,
        margin,
        pageBreak: "auto",
        tableWidth: "wrap",
        styles: {
          font: "THSarabun",
          fontSize: 10,
          cellPadding: 3,
          overflow: "linebreak",
          lineColor: [220, 220, 220],
          lineWidth: 0.2,
          valign: "middle",
        },
        headStyles: {
          fillColor: [240, 240, 240],
          textColor: 20,
          halign: "center",
        },
        columnStyles: col,
        didDrawPage: (data) => {
          if (data.pageNumber > pageNo) {
            pageNo = data.pageNumber;
            drawHeader();
            drawFooter(pageNo);
          }
        },
      });

      const s = this.summary;
      if (s) {
        doc.autoTable({
          startY: (doc.lastAutoTable ? doc.lastAutoTable.finalY : margin.top) + 6,
          theme: "plain",
          margin,
          styles: { font: "THSarabun", fontSize: 12, cellPadding: 6 },
          body: [
            ["", "", "", "", "", "รวมทั้งหมด", money(s.total_subtotal), money(s.total_vat), money(s.total_grand_total)],
          ],
        });
      }

      const fn = `รายงาน_VAT_ขาย_${new Date().toLocaleDateString("th-TH")}.pdf`;
      doc.save(fn);
    },

    exportExcel() {
      if (!this.items || this.items.length === 0) {
        this.$bvToast.toast("ไม่มีข้อมูลให้ส่งออก", {
          title: "Export Excel",
          variant: "warning",
          solid: true,
        });
        return;
      }

      const rows = this.items.map((it, i) => ({
        "#": i + 1,
        "วันที่": it.document_date,
        "เลขที่เอกสาร": it.document_no || "-",
        "ประเภท": it.type === "abb" ? "ย่อ" : "เต็มรูปแบบ",
        "ชื่อลูกค้า/บริษัท": it.company_name || "-",
        "เลขผู้เสียภาษี": it.tax_id || "-",
        "มูลค่าสินค้า": +parseFloat(it.subtotal || 0),
        "VAT": +parseFloat(it.vat || 0),
        "รวมทั้งสิ้น": +parseFloat(it.grand_total || 0),
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      ws["!cols"] = [
        { wch: 4 },
        { wch: 12 },
        { wch: 18 },
        { wch: 12 },
        { wch: 24 },
        { wch: 18 },
        { wch: 14 },
        { wch: 12 },
        { wch: 14 },
      ];

      const ref = XLSX.utils.decode_range(ws["!ref"]);
      for (let R = ref.s.r + 1; R <= ref.e.r; R++) {
        for (const C of [6, 7, 8]) {
          const cell = XLSX.utils.encode_cell({ r: R, c: C });
          if (ws[cell]) ws[cell].z = "#,##0.00";
        }
      }

      if (this.summary) {
        const totalRow = ref.e.r + 2;
        ws[XLSX.utils.encode_cell({ r: totalRow, c: 5 })] = { t: "s", v: "รวมทั้งหมด" };
        ws[XLSX.utils.encode_cell({ r: totalRow, c: 6 })] = { t: "n", v: +this.summary.total_subtotal, z: "#,##0.00" };
        ws[XLSX.utils.encode_cell({ r: totalRow, c: 7 })] = { t: "n", v: +this.summary.total_vat, z: "#,##0.00" };
        ws[XLSX.utils.encode_cell({ r: totalRow, c: 8 })] = { t: "n", v: +this.summary.total_grand_total, z: "#,##0.00" };
      }

      ws["!autofilter"] = { ref: ws["!ref"] };

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "VAT ขาย");
      const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const fn = `รายงาน_VAT_ขาย_${new Date().toLocaleDateString("th-TH")}.xlsx`;
      saveAs(new Blob([wbout], { type: "application/octet-stream" }), fn);
    },
  },
  mounted() {},
};
